import * as React from "react";

import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import {
  SIDEBAR_TABLET_HEADER_CTA,
  SIDEBAR_TABLET_HEADER_LIST,
  SIDEBAR_TABLET_HEADER_ROW,
  SIDEBAR_TABLET_HEADER_ROW_GAP16,
  SIDEBAR_TABLET_HEADER_TITLE,
} from "@/components/shared/main/layout";

/**
 * Canonical sidebar primitives for the HKEJ v2 main site.
 *
 * The bordered card shell (`1px solid rgba(0,0,0,0.3)` + 8px radius + 20px
 * padding) plus its "lighter" section title and the brand-green outline button
 * were re-implemented as private helpers in ~6 files (wealth sidebar-widgets,
 * category-finance-list, property sidebar, search, columns-sub, home). These are
 * the single source of truth; the per-vertical copies now re-export / compose
 * these. Values are the v2 token-based canonical look (the project's V2 design
 * direction) — a few home outliers normalize onto these.
 */

/** Bordered white card used by virtually every sidebar widget. */
export function CardShell({
  children,
  gap = 20,
  padding = 20,
  className = "flex flex-col items-start w-full",
}: {
  children: React.ReactNode;
  gap?: number;
  padding?: number;
  className?: string;
}) {
  return (
    <section
      className={className}
      style={{
        border: "1px solid rgba(0,0,0,0.3)",
        borderRadius: 8,
        padding,
        gap,
        backgroundColor: "#ffffff",
      }}
    >
      {children}
    </section>
  );
}

export type SidebarTitleVariant = "light" | "strong" | "boldSmall" | "uppercase";

function titleStyle(variant: SidebarTitleVariant): React.CSSProperties {
  switch (variant) {
    case "strong":
      return {
        color: CORE_COLORS.textPrimary,
        fontFamily: CORE_FONT.sans,
        fontSize: 20,
        fontWeight: 700,
        lineHeight: "normal",
        margin: 0,
      };
    case "boldSmall":
      return {
        color: CORE_COLORS.textPrimary,
        fontFamily: CORE_FONT.sans,
        fontSize: 18,
        fontWeight: 700,
        lineHeight: "normal",
        margin: 0,
      };
    case "uppercase":
      return {
        color: CORE_COLORS.textPrimary,
        fontFamily: CORE_FONT.condensed,
        fontSize: 14,
        fontWeight: 700,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        lineHeight: "normal",
        margin: 0,
      };
    case "light":
    default:
      return {
        color: "rgba(0,0,0,0.5)",
        fontFamily: CORE_FONT.sans,
        fontSize: 20,
        fontWeight: 400,
        lineHeight: "normal",
        margin: 0,
      };
  }
}

/** Sidebar card heading. `light` (20/400/grey) is the canonical default. */
export function SidebarSectionTitle({
  children,
  variant = "light",
  className,
}: {
  children: React.ReactNode;
  variant?: SidebarTitleVariant;
  className?: string;
}) {
  return (
    <h2 className={className} style={titleStyle(variant)}>
      {children}
    </h2>
  );
}

/**
 * Brand-green "查看更多 / 查看信報視頻" button. Renders an <a> when `href` is
 * given, else a <button>. `filled` switches to the solid brand-green-dark style.
 */
export function OutlineButton({
  label,
  href,
  onClick,
  filled = false,
  className = "self-start",
}: {
  label: string;
  href?: string;
  onClick?: () => void;
  filled?: boolean;
  className?: string;
}) {
  const style: React.CSSProperties = filled
    ? {
        backgroundColor: CORE_COLORS.brandGreenDark,
        borderRadius: 4,
        padding: "7px 8px",
        fontFamily: CORE_FONT.condensed,
        fontSize: 14,
        color: "#ffffff",
        textDecoration: "none",
        display: "inline-block",
      }
    : {
        border: `1px solid ${CORE_COLORS.brandGreen}`,
        borderRadius: 2,
        padding: "8px 9px",
        fontFamily: CORE_FONT.condensed,
        fontSize: 14,
        color: CORE_COLORS.brandGreen,
        textDecoration: "none",
        display: "inline-block",
      };
  const cls = `${className} text-center ${filled ? "hover:opacity-90" : "hover:bg-[#f5fbf8]"} transition-colors`;
  if (href) {
    return (
      <a href={href} className={cls} style={style}>
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls} style={style}>
      {label}
    </button>
  );
}

/**
 * Card body that lifts the "view more" CTA onto the title's row on the tablet
 * band, and leaves mobile and the `lg` sidebar exactly as they were.
 *
 * Below `lg` a sidebar widget spans the full CONTENT width (see `SEGMENT_GRID`),
 * so a CTA sized for a 300px rail ended up alone on a ~700–980px row with a
 * multi-column list above it — a band of empty space under every sidebar card
 * on every page. QA filed it on 信報視頻 and 推介專欄 first
 * (「如果需要讓用戶查看更加多嘅視頻可以放在右上角而不是左下角導致大量空間留白」)
 * and then generalised it: "for any components with layout similar to
 * 通識理財/即時/編輯精選/專欄作者, for tablet size only, move the view more
 * button to the right of the title".
 *
 * It is a WRAP, not a reorder of the source: title and CTA are row 1
 * (`justify-between`), and because the list is `w-full` it is pushed onto row 2.
 * So the DOM order stays title → list → CTA — which is what mobile and `lg`
 * render, and what a screen reader gets at every size — while only the tablet
 * band moves anything. The `order-*` utilities in the constants merely put the
 * CTA before the list within that wrap.
 *
 * Pass the card's OWN title element and CTA element; this owns the layout, not
 * their styling, so each widget keeps its heading level and its button recipe.
 * The 20px rhythm lives here, so a card adopting this should drop its own
 * `gap-[20px]` (or pass `gap={0}` to `CardShell`) rather than stack the two.
 */
export function SidebarCardBody({
  title,
  cta,
  children,
  gap = 20,
}: {
  title: React.ReactNode;
  /** Omit for a card with no "view more" — the wrap then costs nothing. */
  cta?: React.ReactNode;
  children: React.ReactNode;
  /** The card's own vertical rhythm; 16 for the cards that already used it. */
  gap?: 16 | 20;
}) {
  return (
    <div
      className={
        gap === 16 ? SIDEBAR_TABLET_HEADER_ROW_GAP16 : SIDEBAR_TABLET_HEADER_ROW
      }
    >
      <div className={SIDEBAR_TABLET_HEADER_TITLE}>{title}</div>
      <div className={SIDEBAR_TABLET_HEADER_LIST}>{children}</div>
      {cta ? <div className={SIDEBAR_TABLET_HEADER_CTA}>{cta}</div> : null}
    </div>
  );
}
